import moment from 'moment';
import type { TagRequestItem } from '@/client/types/tag-requests';
import { ITEM_STATUS_BADGE, REJECT_REASONS, humanizeCode, titleCaseTag } from './tagRequestStatus';

export type DraftVerdict = 'pending' | 'approved' | 'rejected';

export interface TagDecisionDraft {
  verdict: DraftVerdict;
  reason: string;
  notes: string;
}

export const EMPTY_DRAFT: TagDecisionDraft = { verdict: 'pending', reason: '', notes: '' };

const VERDICTS: { id: DraftVerdict; label: string; active: string }[] = [
  { id: 'pending', label: 'Leave pending', active: 'bg-gray-700 text-white' },
  { id: 'approved', label: 'Approve', active: 'bg-green-600 text-white' },
  { id: 'rejected', label: 'Reject', active: 'bg-red-600 text-white' },
];

interface TagDecisionRowProps {
  item: TagRequestItem;
  draft: TagDecisionDraft;
  onChange: (draft: TagDecisionDraft) => void;
  /** False when the viewer lacks decide permissions or the request is closed. */
  editable: boolean;
}

const DecidedSummary = ({ item }: { item: TagRequestItem }) => (
  <div className="mt-1 space-y-0.5 text-xs text-gray-600">
    <p>
      {item.decided_by === 'automation' ? 'Decided automatically' : `Decided by ${item.decided_by || 'admin'}`}
      {item.confidence != null ? ` · ${Math.round(item.confidence * 100)}% confidence` : ''}
      {item.date_decided ? ` · ${moment(item.date_decided).format('YYYY-MM-DD HH:mm')}` : ''}
    </p>
    {item.decision_reason && <p>Reason: {humanizeCode(item.decision_reason)}</p>}
    {item.reviewer_notes && <p>“{item.reviewer_notes}”</p>}
  </div>
);

const TagDecisionRow = ({ item, draft, onChange, editable }: TagDecisionRowProps) => {
  const pending = item.status === 'pending';
  const set = (patch: Partial<TagDecisionDraft>) => onChange({ ...draft, ...patch });

  return (
    <li className="rounded-lg border border-gray-200 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">{titleCaseTag(item.tag_name)}</p>
          <p className="text-xs text-gray-500">tag #{item.tagid}</p>
        </div>
        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${ITEM_STATUS_BADGE[item.status]}`}>
          {item.status}
        </span>
      </div>

      {!pending && <DecidedSummary item={item} />}

      {pending && editable && (
        <div className="mt-3 space-y-2">
          <div className="inline-flex rounded-md bg-gray-100 p-0.5" role="radiogroup" aria-label={`Decision for ${item.tag_name}`}>
            {VERDICTS.map((v) => (
              <button
                key={v.id}
                type="button"
                role="radio"
                aria-checked={draft.verdict === v.id}
                onClick={() => set({ verdict: v.id })}
                className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                  draft.verdict === v.id ? v.active : 'text-gray-600 hover:bg-white'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>

          {draft.verdict === 'rejected' && (
            <select
              value={draft.reason}
              onChange={(e) => set({ reason: e.target.value })}
              className="block w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
              aria-label="Rejection reason"
            >
              <option value="">Choose a reason…</option>
              {REJECT_REASONS.map((r) => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
          )}

          {draft.verdict !== 'pending' && (
            <textarea
              value={draft.notes}
              onChange={(e) => set({ notes: e.target.value })}
              rows={2}
              placeholder={
                draft.verdict === 'approved'
                  ? 'Optional — what you checked (e.g. GSA cert verified against the registry)'
                  : 'Shown to the lister — tell them what would change the answer'
              }
              className="block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            />
          )}
        </div>
      )}
    </li>
  );
};

export default TagDecisionRow;
